<template>
    <DashboardLayout>
        <div class="d-flex justify-content-center">
            <div class="budget-dashboard">
                <!-- Header -->
                <section class="dash-header d-flex justify-content-between align-items-center">
                    <h1 class="dash-title mb-0">Budget Performance Dashboard</h1>
                    <div class="d-flex gap-2">
                        <!-- Month -->
                        <div class="month-dropdown">
                            <select class="period-btn d-flex align-items-center" v-model="selectedPeriodType"
                                @change="fetchDashboardData">
                                <option value="">Select Period</option>
                                <option v-for="period in periodTypes" :key="period.value" :value="period.value">
                                    {{ period.label }}
                                </option>
                            </select>
                        </div>
                        <!-- Year -->
                        <div class="year-dropdown">
                            <select class="period-btn d-flex align-items-center" v-model="selectedYear"
                                @change="fetchDashboardData">
                                <option value="">Select Year</option>
                                <option v-for="year in yearOptions" :key="year" :value="year">{{ year }}</option>
                            </select>
                        </div>
                    </div>
                </section>

                <!-- Stat cards -->
                <section class="stat-row">
                    <div class="stat-card" v-for="stat in statCards" :key="stat.label">
                        <div class="d-flex justify-content-between align-items-start">
                            <p class="stat-label">{{ stat.label }}</p>
                            <div class="stat-icon" :class="stat.iconClass">
                                <span class="material-symbols-outlined">{{ stat.icon }}</span>
                            </div>
                        </div>
                        <h2 class="stat-value">{{ stat.value }}</h2>
                        <div class="stat-track" :class="stat.trackClass">
                            <div class="stat-fill" :class="stat.fillClass"
                                :style="{ width: clampPercent(stat.percent) + '%' }"></div>
                        </div>
                        <p class="stat-caption">{{ stat.caption }}</p>
                    </div>
                </section>

                <!-- Sector table + MDA bars -->
                <section class="mid-row">
                    <div class="panel">
                        <h3>Budget Performance by Sector</h3>
                        <div v-if="loadingDashboard" class="loading-state text-center py-5">
                            <div class="spinner-border text-success" role="status" aria-hidden="true"></div>
                            <p class="mt-2 mb-0 text-muted">Loading sector performance...</p>
                        </div>
                        <div v-else class="perf-table-wrapper">
                            <table class="perf-table">
                                <thead>
                                    <tr>
                                        <th scope="col">Ministry/Sector</th>
                                        <th scope="col" class="text-center">Released</th>
                                        <th scope="col" class="text-center">Remaining</th>
                                        <th scope="col" class="text-center">Percentage</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="s in sectorPerformance" :key="s.label">
                                        <td>{{ s.label }}</td>
                                        <td class="text-center">{{ formatCompactCurrency(s.released) }}</td>
                                        <td class="text-center cell-green">{{ formatCompactCurrency(s.remaining) }}</td>
                                        <td class="text-center cell-green">{{ formatPercent(s.percentage) }}</td>
                                    </tr>
                                    <tr v-if="sectorPerformance.length === 0">
                                        <td colspan="4" class="text-center">No sector data to display.</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div class="panel">
                        <h3>Budget Performance by MDA</h3>
                        <div v-if="loadingDashboard" class="loading-state text-center py-5">
                            <div class="spinner-border text-success" role="status" aria-hidden="true"></div>
                            <p class="mt-2 mb-0 text-muted">Loading MDA performance...</p>
                        </div>
                        <div v-else class="mda-list">
                            <div class="mda-row" v-for="m in topMdaBars" :key="m.name">
                                <div class="mda-chip">{{ formatPercent(m.percentage) }}</div>
                                <div class="mda-body">
                                    <div class="mda-top">
                                        <span class="mda-name">{{ m.name }}</span>
                                        <span class="mda-amount">{{ formatCompactCurrency(m.budget) }}</span>
                                    </div>
                                    <div class="progress-track">
                                        <div class="progress-fill"
                                            :style="{ width: clampPercent(m.percentage) + '%' }"></div>
                                    </div>
                                </div>
                            </div>
                            <p v-if="topMdaBars.length === 0" class="text-center mb-0">No MDA data to display.</p>
                        </div>
                    </div>
                </section>

                <!-- MDA table + implementation score -->
                <section class="bottom-row">
                    <div class="panel">
                        <h3>Budget Performance by MDA</h3>
                        <div v-if="loadingDashboard" class="loading-state text-center py-5">
                            <div class="spinner-border text-success" role="status" aria-hidden="true"></div>
                            <p class="mt-2 mb-0 text-muted">Loading MDA budget...</p>
                        </div>
                        <div v-else class="perf-table-wrapper">
                            <table class="perf-table perf-table-fixed">
                                <thead>
                                    <tr>
                                        <th scope="col">Ministry/Agency</th>
                                        <th scope="col">Budget</th>
                                        <th scope="col">Released</th>
                                        <th scope="col">Percentage</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="m in mdaPerformance" :key="m.name">
                                        <td>{{ m.name }}</td>
                                        <td>{{ formatCompactCurrency(m.budget) }}</td>
                                        <td>{{ formatCompactCurrency(m.released) }}</td>
                                        <td class="cell-green">{{ formatPercent(m.percentage) }}</td>
                                    </tr>
                                    <tr v-if="mdaPerformance.length === 0">
                                        <td colspan="4" class="text-center">No MDA data to display.</td>
                                    </tr>
                                </tbody>
                                <tbody class="report-body">
                                    <tr class="report-title">
                                        <td colspan="4">Budget Implementation Report</td>
                                    </tr>
                                    <tr>
                                        <td>Total Budget</td>
                                        <td>{{ formatCompactCurrency(totalBudget) }}</td>
                                        <td>{{ formatCompactCurrency(fundsReleased) }}</td>
                                        <td class="cell-green">{{ formatPercent(releasedPercent) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div class="panel panel-donut">
                        <h3>Implementation Score of Budget by MDA</h3>
                        <div v-if="loadingDashboard" class="loading-state text-center py-5">
                            <div class="spinner-border text-success" role="status" aria-hidden="true"></div>
                        </div>
                        <template v-else>
                            <div class="donut-wrapper">
                                <div class="donut" :style="{ background: donutGradient }">
                                    <div class="donut-center">
                                        <h2>{{ formatPercent(implementationScore) }}</h2>
                                        <span>IMPLEMENTED</span>
                                    </div>
                                </div>
                            </div>
                            <div class="donut-legend">
                                <div class="legend-item">
                                    <span class="legend-dot dot-implemented"></span>
                                    Implemented: {{ formatCompactCurrency(implementedAmount) }}
                                </div>
                                <div class="legend-item">
                                    <span class="legend-dot dot-remaining"></span>
                                    Remaining: {{ formatCompactCurrency(fundsRemaining) }}
                                </div>
                            </div>
                        </template>
                    </div>
                </section>
            </div>
        </div>
    </DashboardLayout>
</template>

<script setup>
import { ref, computed, onMounted, defineOptions } from 'vue'
import axios from 'axios'
import Cookies from 'js-cookie'
import DashboardLayout from '../../layout/DashboardLayout.vue'

defineOptions({
    name: 'BudgetPerformanceDashboardPage',
})

const root_api_url = process.env.VUE_APP_API_ROOT_URL
const loadingDashboard = ref(true)

/* ------------------------------------------------------------------
   Period filters
------------------------------------------------------------------- */
const periodTypes = [
    { value: 'MONTHLY', label: 'Monthly' },
    { value: 'QUARTERLY', label: 'Quarterly' },
]
const currentYear = new Date().getFullYear()
const yearOptions = [currentYear, currentYear - 1, currentYear - 2]

const selectedPeriodType = ref('MONTHLY')
const selectedYear = ref(currentYear)

/* ------------------------------------------------------------------
   State
------------------------------------------------------------------- */
const totalBudget = ref(0)
const fundsReleased = ref(0)
const fundsRemaining = ref(0)
const implementedAmount = ref(0)
const implementationScore = ref(0)
const sectorPerformance = ref([]) // [{ label, released, remaining, percentage }]
const mdaPerformance = ref([]) // [{ name, budget, released, percentage }]

/* ------------------------------------------------------------------
   Derived values
------------------------------------------------------------------- */
const releasedPercent = computed(() =>
    totalBudget.value ? (fundsReleased.value / totalBudget.value) * 100 : 0
)
const remainingPercent = computed(() =>
    totalBudget.value ? (fundsRemaining.value / totalBudget.value) * 100 : 0
)

const statCards = computed(() => [
    {
        label: 'Total Budget',
        value: formatCompactCurrency(totalBudget.value),
        icon: 'account_balance',
        iconClass: 'icon-green',
        trackClass: 'track-green',
        fillClass: 'fill-green',
        percent: releasedPercent.value,
        caption: `${formatPercent(releasedPercent.value)} released`,
    },
    {
        label: 'Funds Released',
        value: formatCompactCurrency(fundsReleased.value),
        icon: 'payments',
        iconClass: 'icon-green',
        trackClass: 'track-green',
        fillClass: 'fill-green',
        percent: releasedPercent.value,
        caption: `${formatPercent(releasedPercent.value)} released`,
    },
    {
        label: 'Funds Remaining',
        value: formatCompactCurrency(fundsRemaining.value),
        icon: 'error',
        iconClass: 'icon-red',
        trackClass: 'track-red',
        fillClass: 'fill-red',
        percent: remainingPercent.value,
        caption: `${formatPercent(remainingPercent.value)} remaining`,
    },
])

// Right-hand "by MDA" bars: the five best-performing MDAs
const topMdaBars = computed(() =>
    [...mdaPerformance.value].sort((a, b) => b.percentage - a.percentage).slice(0, 5)
)

// Donut: light "remaining" slice first, implemented slice fills the rest
const donutGradient = computed(() => {
    const score = clampPercent(implementationScore.value)
    return `conic-gradient(#DDEFD9 0 ${100 - score}%, #28A745 ${100 - score}% 100%)`
})

/* ------------------------------------------------------------------
   Helpers
------------------------------------------------------------------- */
function clampPercent(val) {
    const n = Number(val) || 0
    return Math.min(Math.max(n, 0), 100)
}

function formatPercent(val) {
    return `${Math.round(Number(val) || 0)}%`
}

function formatCompactCurrency(val) {
    if (val === '' || val === null || val === undefined) return '₦0'
    const num = Number(val)
    const abs = Math.abs(num)
    let display
    if (abs >= 1_000_000_000) display = (num / 1_000_000_000).toFixed(1) + 'B'
    else if (abs >= 1_000_000) display = (num / 1_000_000).toFixed(1) + 'M'
    else if (abs >= 1_000) display = (num / 1_000).toFixed(1) + 'K'
    else display = num.toFixed(0)
    return '₦' + display
}


const applyDashboard = ({ summary, sectors, mdas }) => {
    totalBudget.value = summary.totalBudget
    fundsReleased.value = summary.fundsReleased
    fundsRemaining.value = summary.fundsRemaining
    implementedAmount.value = summary.implementedAmount
    implementationScore.value = summary.implementationScore

    sectorPerformance.value = sectors.map((s) => ({
        label: s.sector,
        released: s.released,
        remaining: s.remaining,
        percentage: s.percentage,
    }))

    mdaPerformance.value = mdas.map((m) => ({
        name: m.mdaName,
        budget: m.budget,
        released: m.released,
        percentage: m.percentage,
    }))
}

const fetchDashboardData = async () => {
    loadingDashboard.value = true
    try {
        const token = Cookies.get('auth_token')
        const { data } = await axios.get(`${root_api_url}/appraisal/dashboard`, {
            params: { year: selectedYear.value, periodType: selectedPeriodType.value },
            headers: { Authorization: `Bearer ${token}` },
        })
        applyDashboard(data)
        console.log('Fetched budget performance data:', data)
    } catch (error) {
        console.error('Error fetching budget performance data:', error)
    } finally {
        loadingDashboard.value = false
    }
}

onMounted(async () => {
    await fetchDashboardData()
    document.title = 'Budget Performance Dashboard'
})
</script>

<style scoped>
.budget-dashboard {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    width: calc(100% - 3rem);
    margin: 0 auto;
}

/* Header */
.dash-header {
    margin-bottom: 0.5rem;
}

.dash-title {
    font-size: 2rem;
    font-weight: 700;
    color: #1E7A34;
}

.period-btn {
    background: var(--bg);
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 0.5rem 1rem;
    font-weight: 500;
    color: var(--color);
}

/* Stat cards */
.stat-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;
}

@media (max-width: 768px) {
    .stat-row {
        grid-template-columns: 1fr;
    }
}

.stat-icon {
    width: 42px;
    height: 42px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.stat-icon.icon-green {
    background: #E6F4EA;
    color: #28A745;
}

.stat-icon.icon-red {
    background: #FBE7E9;
    color: #DC3545;
}

.stat-track {
    height: 10px;
    border-radius: 999px;
    overflow: hidden;
}

.track-green {
    background: #EAF3E6;
}

.track-red {
    background: #FBE7E9;
}

.stat-fill {
    height: 100%;
    border-radius: 999px;
}

.fill-green {
    background: #28A745;
}

.fill-red {
    background: #DC3545;
}

.stat-caption {
    text-align: right;
    font-size: 0.72rem;
    font-weight: 600;
    color: #6c757d;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    margin: 0.9rem 0 0;
}

/* Panels */
.panel h3 {
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 1.5rem;
}

.mid-row {
    display: grid;
    grid-template-columns: 1.15fr 1fr;
    gap: 1.5rem;
}

.bottom-row {
    display: grid;
    grid-template-columns: 2.5fr 1fr;
    gap: 1.5rem;
}

@media (max-width: 992px) {

    .mid-row,
    .bottom-row {
        grid-template-columns: 1fr;
    }
}

/* Tables */
.perf-table-wrapper {
    overflow-x: auto;
}

.perf-table {
    width: 100%;
    border-collapse: collapse;
}

.perf-table-fixed {
    table-layout: fixed;
}

.perf-table .cell-green {
    color: #1E7A34;
}

/* MDA bars */
.mda-row {
    display: flex;
    align-items: center;
    gap: 0.9rem;
    margin-bottom: 1rem;
}

.mda-row:last-child {
    margin-bottom: 0;
}

.mda-chip {
    flex: 0 0 56px;
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #E6F4EA;
    color: #28A745;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 500;
}

.mda-body {
    flex: 1;
    min-width: 0;
}

.mda-top {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    font-size: 0.9rem;
    color: var(--color);
}

.mda-amount {
    font-size: 0.8rem;
}

.progress-track {
    height: 8px;
    background: var(--chart-track);
    border-radius: 999px;
    margin-top: 0.4rem;
    overflow: hidden;
}

.progress-fill {
    height: 100%;
    background: #28A745;
    border-radius: 999px;
}

/* Donut */
.donut-wrapper {
    display: flex;
    justify-content: center;
    margin: 1.5rem 0;
}

.donut {
    width: 240px;
    height: 240px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
}

.donut-center {
    position: relative;
    z-index: 1;
    text-align: center;
}

.donut-center h2 {
    font-size: 3rem;
    font-weight: 700;
    margin: 0;
    line-height: 1;
}

.donut-legend {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 1.5rem;
    font-size: 0.85rem;
    font-weight: 500;
}

.legend-item {
    display: flex;
    align-items: center;
}

.legend-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    display: inline-block;
    margin-right: 0.5rem;
}

.dot-implemented {
    background: #28A745;
}

.dot-remaining {
    background: #DDEFD9;
}
</style>